import { QueryParam } from "@/utils/hooks/url/use-query-state";
import { type RowData } from "@tanstack/react-table";

import {
  BASIC_TABLE_V3_HEADER_COLUMN_ID_ATTRIBUTE,
  DEFAULT_VIRTUALIZATION_OVERSCAN,
  DROP_TARGET_AFTER_CLASS,
  DROP_TARGET_BEFORE_CLASS,
  MENU_DROP_TARGET_AFTER_CLASS,
  MENU_DROP_TARGET_BEFORE_CLASS,
} from "./BasicTableV3.constants";

export { getNearestVerticalScrollElement } from "@/utils/scroll-utils";
import {
  type BasicTableV3Column,
  type BasicTableV3ColumnFilterConfig,
  type BasicTableV3ColumnScopedFilter,
  type BasicTableV3ColumnSection,
  type BasicTableV3DisplayColumn,
  type BasicTableV3DragSurface,
  type BasicTableV3DragTarget,
  type BasicTableV3DropPlacement,
  type BasicTableV3FilterOperator,
  type BasicTableV3ResolvedFilterColumn,
  type BasicTableV3State,
  type UseBasicTableV3StateOptions,
} from "./BasicTableV3.types";

export const supportsBasicTableV3HeaderFilter = <TData extends RowData>(
  filterColumn: BasicTableV3ResolvedFilterColumn<TData> | undefined,
  canCreateFilter: boolean,
) =>
  canCreateFilter &&
  filterColumn != null &&
  (filterColumn.filterConfig.type === "string" ||
    filterColumn.filterConfig.allowCustomValues === true ||
    filterColumn.resolvedValueOptions.length > 0);

export const getScrollMargin = (anchor: HTMLElement | null, scrollElement: HTMLElement | null) => {
  if (typeof window === "undefined" || anchor == null) {
    return 0;
  }

  if (scrollElement == null) {
    return anchor.getBoundingClientRect().top + window.scrollY;
  }

  return (
    anchor.getBoundingClientRect().top -
    scrollElement.getBoundingClientRect().top +
    scrollElement.scrollTop
  );
};

const HORIZONTAL_SCROLL_EDGE_TOLERANCE_PX = 4;

export const getBasicTableV3HorizontalScrollAffordances = ({
  clientWidth,
  scrollLeft,
  scrollWidth,
}: {
  clientWidth: number;
  scrollLeft: number;
  scrollWidth: number;
}) => {
  const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
  const hasHorizontalOverflow = maxScrollLeft > HORIZONTAL_SCROLL_EDGE_TOLERANCE_PX;

  return {
    hasScrollableLeft: hasHorizontalOverflow && scrollLeft > HORIZONTAL_SCROLL_EDGE_TOLERANCE_PX,
    hasScrollableRight:
      hasHorizontalOverflow && maxScrollLeft - scrollLeft > HORIZONTAL_SCROLL_EDGE_TOLERANCE_PX,
  };
};

// Must match the attributes stamped on the rendered table in BasicTableV3.tsx.
const BASIC_TABLE_V3_ROOT_SELECTOR = "[data-basic-table-v3-root]";
const BASIC_TABLE_V3_BODY_CELL_COLUMN_ID_ATTRIBUTE = "data-basic-table-v3-column-id";

const getBasicTableV3CellColumnId = (cell: HTMLTableCellElement) =>
  cell.tagName === "TD"
    ? cell.getAttribute(BASIC_TABLE_V3_BODY_CELL_COLUMN_ID_ATTRIBUTE)
    : cell.getAttribute(BASIC_TABLE_V3_HEADER_COLUMN_ID_ATTRIBUTE);

/**
 * Intrinsic (max-content) widths of rendered header and body cells, used by
 * resize-handle double-click and Markdown content sizing. Cells are cloned into
 * a hidden measurer so the live table's fixed widths never constrain the result, and hover-revealed
 * header chrome (CSS `group-hover` reveal) stays collapsed on the unhovered
 * clone. Only currently rendered rows are measured, so under virtualization the
 * fit tracks the rendered window rather than the full data set.
 *
 * Cost scales with (rendered rows x measured columns), so callers must keep the
 * column set small; the clones are spread into one `replaceChildren` call,
 * which overflows the argument limit past ~130k cells.
 */
export const measureBasicTableV3ColumnsAutoFitWidths = (
  root: HTMLElement | null,
  columnIds: readonly string[],
): Readonly<Record<string, number>> => {
  if (root == null || columnIds.length === 0) {
    return {};
  }

  const columnIdSet = new Set(columnIds);
  const cellsWithColumnIds = [
    ...root.querySelectorAll<HTMLTableCellElement>(
      `th[${BASIC_TABLE_V3_HEADER_COLUMN_ID_ATTRIBUTE}], td[${BASIC_TABLE_V3_BODY_CELL_COLUMN_ID_ATTRIBUTE}]`,
    ),
  ].flatMap((cell) => {
    const columnId = getBasicTableV3CellColumnId(cell);
    return columnId != null &&
      columnIdSet.has(columnId) &&
      // A table can be rendered inside a cell; its cells belong to the inner
      // table root and must not leak into the outer column's measurement.
      cell.closest(BASIC_TABLE_V3_ROOT_SELECTOR) === root
      ? [{ cell, columnId }]
      : [];
  });

  if (cellsWithColumnIds.length === 0) {
    return {};
  }

  const measurer = document.createElement("div");
  measurer.style.position = "absolute";
  measurer.style.top = "0";
  measurer.style.left = "0";
  measurer.style.visibility = "hidden";
  measurer.style.pointerEvents = "none";
  // Appended inside the root so the clones keep the same inherited styles
  // (font, dark mode) they render with in the live table.
  root.appendChild(measurer);

  try {
    // All clones are appended before any rect is read: interleaving writes
    // and reads would force one synchronous reflow per cell, while a single
    // write followed by read-only passes lays out once. Each clone is
    // measured individually, so line wrapping between the inline-blocks
    // cannot affect any clone's own intrinsic width.
    const clonesWithColumnIds = cellsWithColumnIds.map(({ cell, columnId }) => {
      const clone = cell.cloneNode(true) as HTMLTableCellElement;
      // Shed the live cell's fixed layout so the clone shrink-wraps its
      // content; padding still applies from the cell's own classes, and
      // absolutely-positioned chrome (resize handles, separators) adds no
      // width.
      clone.style.width = "max-content";
      clone.style.left = "auto";
      clone.style.right = "auto";
      clone.style.display = "inline-block";
      return { clone, columnId };
    });
    measurer.replaceChildren(...clonesWithColumnIds.map(({ clone }) => clone));

    const maxWidthByColumnId = new Map<string, number>();
    for (const { clone, columnId } of clonesWithColumnIds) {
      maxWidthByColumnId.set(
        columnId,
        Math.max(
          maxWidthByColumnId.get(columnId) ?? 0,
          Math.ceil(clone.getBoundingClientRect().width),
        ),
      );
    }

    return Object.fromEntries([...maxWidthByColumnId].filter(([, maxWidth]) => maxWidth > 0));
  } finally {
    measurer.remove();
  }
};

export const measureBasicTableV3ColumnAutoFitWidth = (
  root: HTMLElement | null,
  columnId: string,
): number | null => measureBasicTableV3ColumnsAutoFitWidths(root, [columnId])[columnId] ?? null;

interface BasicTableV3HorizontalWheelActionInput {
  clientWidth: number;
  deltaX: number;
  deltaY: number;
  /** True when JS-mirrored scrollers (the full-page sticky header and
   *  floating scrollbar) must move in lockstep with the body. */
  hasScrollMirrors: boolean;
  scrollLeft: number;
  scrollWidth: number;
  shiftKey: boolean;
}

interface BasicTableV3HorizontalWheelAction {
  nextScrollLeft: number;
  shouldPreventDefault: boolean;
}

export const getBasicTableV3HorizontalWheelAction = ({
  clientWidth,
  deltaX,
  deltaY,
  hasScrollMirrors,
  scrollLeft,
  scrollWidth,
  shiftKey,
}: BasicTableV3HorizontalWheelActionInput): BasicTableV3HorizontalWheelAction | null => {
  const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
  if (maxScrollLeft <= 0) {
    return null;
  }

  const absoluteDeltaX = Math.abs(deltaX);
  const absoluteDeltaY = Math.abs(deltaY);
  const isShiftWheel = shiftKey && deltaY !== 0 && absoluteDeltaY >= absoluteDeltaX;
  const isHorizontalWheel = deltaX !== 0 && absoluteDeltaX >= absoluteDeltaY;
  const horizontalDelta = isShiftWheel ? deltaY : isHorizontalWheel ? deltaX : 0;

  if (horizontalDelta === 0) {
    return null;
  }

  const nextScrollLeft = Math.min(maxScrollLeft, Math.max(0, scrollLeft + horizontalDelta));
  const willReachBoundary = nextScrollLeft <= 0 || maxScrollLeft - nextScrollLeft <= 0;
  const cannotMove = nextScrollLeft === scrollLeft;

  return {
    nextScrollLeft,
    // With JS-mirrored scrollers, drive every horizontal wheel gesture
    // manually rather than letting the native (compositor-thread) scroll
    // run: the mirrors only learn the offset from frame-stale scroll
    // events, so the sticky header visibly chases the rows. Writing both
    // scrollLefts in the wheel handler moves them in the same frame. (The
    // handler still falls back to the native scroll plus a predictive
    // mirror sync when the event is not cancelable.)
    shouldPreventDefault: hasScrollMirrors || isShiftWheel || cannotMove || willReachBoundary,
  };
};

export const reorderColumns = (
  currentOrder: string[],
  draggedColumnId: string,
  targetColumnId: string,
  placement: BasicTableV3DropPlacement,
) => {
  if (draggedColumnId === targetColumnId) {
    return currentOrder;
  }

  const nextOrder = currentOrder.filter((columnId) => columnId !== draggedColumnId);
  const targetIndex = nextOrder.indexOf(targetColumnId);

  if (targetIndex === -1) {
    return currentOrder;
  }

  nextOrder.splice(placement === "after" ? targetIndex + 1 : targetIndex, 0, draggedColumnId);
  return nextOrder;
};

export const getColumnsByPinnedState = <TData>(
  columns: readonly BasicTableV3DisplayColumn<TData>[],
  pinnedColumnIds: ReadonlySet<string>,
) => ({
  frozen: columns.filter((column) => pinnedColumnIds.has(column.id)),
  scrolling: columns.filter((column) => !pinnedColumnIds.has(column.id)),
});

export const getBasicTableV3RightPinnedColumnIds = <TData>(
  columns: readonly BasicTableV3Column<TData>[],
  pinSide?: "left" | "right",
): string[] =>
  columns
    .filter((column) => column.stickyRight || (pinSide === "right" && column.pinned))
    .map((column) => column.id);

export const createBasicTableV3InitialState = <
  TData,
  TFilter extends BasicTableV3ColumnScopedFilter,
>({
  columns,
  initialActiveFilters,
  initialColumnFormats,
  initialColumnLayout,
  initialMode = "table",
  initialSorting,
  pinSide,
}: Pick<
  UseBasicTableV3StateOptions<TData, TFilter>,
  | "columns"
  | "initialActiveFilters"
  | "initialColumnFormats"
  | "initialColumnLayout"
  | "initialMode"
  | "initialSorting"
  | "pinSide"
>): BasicTableV3State<TFilter> => ({
  activeFilters: initialActiveFilters ? [...initialActiveFilters] : [],
  columnFormats: initialColumnFormats ? { ...initialColumnFormats } : {},
  columnOrder: restoredColumnOrder(columns, initialColumnLayout?.columnOrder),
  // Fixed right pins coexist with user-controlled left freezing and win if both are requested.
  columnPinning: {
    ...(pinSide === "right"
      ? {}
      : {
          left: columns
            .filter((column) => column.initialFrozen && !column.stickyRight)
            .map((column) => column.id),
        }),
    ...(pinSide === "right" || columns.some((column) => column.stickyRight)
      ? { right: getBasicTableV3RightPinnedColumnIds(columns, pinSide) }
      : {}),
  },
  columnSizing: Object.fromEntries(
    columns.map((column) => [
      column.id,
      initialColumnLayout?.columnSizing?.[column.id] ?? column.size,
    ]),
  ),
  columnVisibility: Object.fromEntries(
    columns.map((column) => [
      column.id,
      initialColumnLayout?.columnVisibility?.[column.id] ?? !column.initialHidden,
    ]),
  ),
  mode: initialMode,
  sorting: initialSorting ? [...initialSorting] : [],
});

// Restored keys lead, in their stored order; anything they don't name keeps the column list's own
// order behind them. Keys for columns this table doesn't have are ignored.
const restoredColumnOrder = <TData>(
  columns: readonly BasicTableV3Column<TData>[],
  restored: readonly string[] | undefined,
): string[] => {
  const defaultOrder = columns.map((column) => column.id);
  if (restored == null || restored.length === 0) {
    return defaultOrder;
  }
  const known = new Set(defaultOrder);
  const ordered = restored.filter((id) => known.has(id));
  const orderedSet = new Set(ordered);
  return [...ordered, ...defaultOrder.filter((id) => !orderedSet.has(id))];
};

export const getBasicTableV3ColumnVisibilityShorthandById = <TData>(
  columns: readonly BasicTableV3Column<TData>[],
): Record<string, string> => {
  return Object.fromEntries(
    columns.flatMap((column) =>
      column.queryParamShorthand == null
        ? []
        : ([[column.id, column.queryParamShorthand]] as const),
    ),
  );
};

/** Press handlers that activate on left-button mousedown instead of click.
 *  Dismissing an open filter menu happens on document mousedown and can purge
 *  a pending chip, reflowing the row before mouseup — a click-activated
 *  control under the cursor at mousedown time slides away and swallows the
 *  interaction. Activating on mousedown lets the pre-dismissal layout decide
 *  the target. Keyboard activation still works: it arrives as a click with
 *  `detail === 0` and no preceding mousedown. */
export const getBasicTableV3MouseDownPressProps = (onPress: () => void) => ({
  onMouseDown: (event: { button: number; preventDefault: () => void }) => {
    if (event.button !== 0) {
      return;
    }
    // The browser's default focus-on-mousedown runs AFTER this handler; left
    // unprevented it would move focus onto the pressed control, stealing it
    // from the value editor the opened menu just focused.
    event.preventDefault();
    onPress();
  },
  onClick: (event: { detail: number }) => {
    if (event.detail === 0) {
      onPress();
    }
  },
});

/** A zero-count value can't include or exclude any rows, so its menu entry
 *  renders disabled — still listed, keeping the column's full value domain
 *  visible. Currently selected values stay enabled so they can be deselected,
 *  and a missing faceted map means counts are unknown rather than zero. */
export const isBasicTableV3ValueOptionDisabled = (
  facetedValues: Map<unknown, number> | undefined,
  option: string,
  selectedValues: readonly string[],
): boolean =>
  facetedValues != null &&
  (facetedValues.get(option) ?? 0) === 0 &&
  !selectedValues.includes(option);

/** Plural forms for label last words that the suffix rules below get wrong,
 *  mostly labels that are already plural ("Tags" would become "tagses"). Keyed
 *  on the lowercased last word so multi-word labels such as "Query tags" and
 *  "Allowed tools" match too. */
const BASIC_TABLE_V3_PLURAL_OVERRIDES: Readonly<Record<string, string>> = {
  aliases: "aliases",
  datasources: "datasources",
  subscriptions: "subscriptions",
  tags: "tags",
  tools: "tools",
};

/** Value-menu search placeholder: "Agent" → "Search agents", "Branch" →
 *  "Search branches", "Status" → "Search statuses". Labels ending in "by"
 *  already describe the values being searched, so they remain unchanged.
 *
 *  Be careful: this is a naive English pluralizer that assumes the label's last
 *  word is a singular noun. Already-plural labels and irregular nouns come out
 *  wrong ("Tags" → "tagses") unless their last word is listed in
 *  `BASIC_TABLE_V3_PLURAL_OVERRIDES`. When adding a filterable column, check its
 *  placeholder and either add an override or set
 *  `filterConfig.valueSearchPlaceholder`. */
export const getBasicTableV3ValueSearchLabel = (columnLabel: string): string => {
  const entity = columnLabel.trim().toLowerCase();
  if (entity.endsWith(" by")) {
    return `Search ${entity}`;
  }
  const lastWordStart = entity.lastIndexOf(" ") + 1;
  const override = BASIC_TABLE_V3_PLURAL_OVERRIDES[entity.slice(lastWordStart)];
  if (override != null) {
    return `Search ${entity.slice(0, lastWordStart)}${override}`;
  }
  const plural = /(s|x|z|ch|sh)$/.test(entity)
    ? `${entity}es`
    : /[^aeiou]y$/.test(entity)
      ? `${entity.slice(0, -1)}ies`
      : `${entity}s`;
  return `Search ${plural}`;
};

/** Distinguish a typed value from the preset options listed beside it. */
export const getBasicTableV3CustomValueMenuLabel = (valueLabel: string): string =>
  JSON.stringify(valueLabel);

/** Value-menu search matches every piece of option presentation, including its stored value. */
export const matchesBasicTableV3ValueSearch = (
  query: string,
  label: string,
  description: string | undefined,
  group: string | undefined,
  value?: string,
): boolean => {
  const lowerQuery = query.toLowerCase();
  return (
    label.toLowerCase().includes(lowerQuery) ||
    (description?.toLowerCase().includes(lowerQuery) ?? false) ||
    (group?.toLowerCase().includes(lowerQuery) ?? false) ||
    (value?.toLowerCase().includes(lowerQuery) ?? false)
  );
};

/** Keep the authoritative suggestion order stable while making selected
 *  custom values visible so they can be removed from the same menu. */
export const getBasicTableV3ValueMenuOptions = (
  resolvedOptions: readonly string[],
  selectedValues: readonly string[],
): string[] => {
  const resolvedOptionSet = new Set(resolvedOptions);
  return [...selectedValues.filter((value) => !resolvedOptionSet.has(value)), ...resolvedOptions];
};

/** Return the final leading custom option when canonical options follow it.
 *  Filtered menus use this to draw exactly one group boundary. */
export const getBasicTableV3ValueMenuDividerAfterOption = (
  visibleOptions: readonly string[],
  resolvedOptions: readonly string[],
): string | undefined => {
  const resolvedOptionSet = new Set(resolvedOptions);
  const firstCanonicalIndex = visibleOptions.findIndex((option) => resolvedOptionSet.has(option));
  return firstCanonicalIndex > 0 ? visibleOptions[firstCanonicalIndex - 1] : undefined;
};

/** Resolve typed text to a known value, including display labels such as an
 *  agent name whose stored value is an id. Raw values remain case-sensitive. */
export const findBasicTableV3ExactValueOption = (
  options: readonly string[],
  text: string,
  getValueLabel: (value: string) => string,
): string | undefined => {
  const trimmedText = text.trim();
  return (
    options.find((option) => option === trimmedText) ??
    options.find(
      (option) =>
        getValueLabel(option) !== option &&
        getValueLabel(option).toLowerCase() === trimmedText.toLowerCase(),
    )
  );
};

/** Selected values of a filter, treating empty strings as unselected. */
export const getBasicTableV3FilterSelectedValues = (filter: {
  matchesNothing?: boolean;
  value: string | string[];
}): string[] =>
  filter.matchesNothing
    ? []
    : (Array.isArray(filter.value) ? filter.value : [filter.value]).filter(
        (value) => value.trim() !== "",
      );

export const getBasicTableV3FilterOperatorLabel = (
  operator: BasicTableV3FilterOperator,
  { isMultiValue = true }: { isMultiValue?: boolean } = {},
): string =>
  operator === "contains"
    ? "includes"
    : operator === "does not contain"
      ? "excludes"
      : operator === "is any of" && !isMultiValue
        ? "is"
        : operator;

/** Serialized query state may predate the "is any of"/"is not" operator names;
 *  translate the legacy names so old URLs keep deserializing. */
export const normalizeLegacyBasicTableV3FilterOperator = (operator: string): string =>
  operator === "includes" ? "is any of" : operator === "excludes" ? "is not" : operator;

/** A pending filter has no selected values (and isn't matchesNothing): it only
 *  exists while its value menu is open. Filter shapes without value machinery
 *  are never pending. */
export const isBasicTableV3PendingFilter = (filter: {
  columnId: string;
  matchesNothing?: boolean;
  value?: string | string[];
}): boolean =>
  filter.matchesNothing !== true &&
  filter.value != null &&
  getBasicTableV3FilterSelectedValues({ value: filter.value }).length === 0;

/** Enum filters are multi-select by default (checkbox value menus); columns can
 *  still opt out with `isMultiValue: false` — e.g. filters mapped to single-valued
 *  server request fields. */
export const isMultiValueFilterConfig = (
  filterConfig?: Pick<BasicTableV3ColumnFilterConfig, "isMultiValue" | "type">,
): boolean =>
  filterConfig == null ? false : filterConfig.isMultiValue ?? filterConfig.type === "enum";

export const getBasicTableV3ColumnFilterQueryParamMap = <TData>(
  columns: readonly BasicTableV3Column<TData>[],
): Record<string, QueryParam> => {
  return Object.fromEntries(
    columns.flatMap((column) =>
      column.queryParam == null ||
      column.filterConfig == null ||
      column.filterConfig.enabled === false
        ? []
        : ([[column.id, column.queryParam]] as const),
    ),
  );
};

export const getDropTargetClass = (
  columnId: string,
  dragTarget: BasicTableV3DragTarget | null,
  surface: BasicTableV3DragSurface,
): string | false => {
  if (dragTarget?.columnId !== columnId || dragTarget.surface !== surface) {
    return false;
  }

  return dragTarget.placement === "before" ? DROP_TARGET_BEFORE_CLASS : DROP_TARGET_AFTER_CLASS;
};

export const getMenuDropTargetClass = (
  columnId: string,
  dragTarget: BasicTableV3DragTarget | null,
): string | false => {
  if (dragTarget?.columnId !== columnId || dragTarget.surface !== "menu") {
    return false;
  }

  return dragTarget.placement === "before"
    ? MENU_DROP_TARGET_BEFORE_CLASS
    : MENU_DROP_TARGET_AFTER_CLASS;
};

export const getColumnSection = (isPinned: boolean): BasicTableV3ColumnSection =>
  isPinned ? "frozen" : "scrolling";

export { DEFAULT_VIRTUALIZATION_OVERSCAN };
